import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Link } from '@mui/material';

interface ExpandableTextProps {
  text: string;
  maxWidth?: number | string;
  maxLines?: number;
  variant?: 'body1' | 'body2' | 'subtitle1' | 'subtitle2' | 'caption';
  color?: string;
}

export const ExpandableText: React.FC<ExpandableTextProps> = ({ 
  text, 
  maxWidth,
  maxLines = 1,
  variant = 'body2',
  color,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return <Typography component="span" variant={variant} color={color}>N/A</Typography>;

  // Estimate characters per line (approximate)
  const charsPerLine = 60;
  const maxChars = maxLines * charsPerLine;
  const isOverflowing = text.length > maxChars;

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const displayText = !isExpanded && isOverflowing 
    ? `${text.slice(0, maxChars).trim()}...` 
    : text;

  return (
    <Box component="span" sx={{ display: 'inline', maxWidth: maxWidth || 'none' }}>
      <Typography
        component="span"
        variant={variant}
        color={color}
        sx={{
          wordBreak: 'break-word',
          display: 'inline',
        }}
      >
        {displayText}
      </Typography>
      {isOverflowing && (
        <Link
          component="button"
          variant="body2"
          onClick={toggleExpand}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            color: 'primary.main',
            fontSize: '0.875rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            ml: 0.5,
            display: 'inline',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {isExpanded ? 'less' : 'more'}
        </Link>
      )}
    </Box>
  );
};